import { useMemo, useEffect } from "react";
import { useActiveFBOs } from "../containers/ActiveFBOContainer";
import { useIdentity } from "../containers/IdentityContainer";
import { useRoleOverride } from "../stores/useRoleOverride";
import {
  hasPermission,
  Permission,
  PermissionContext,
} from "../utils/permissions";

/**
 * Hook for checking user permissions throughout the application
 *
 * Provides a single, consistent way to check permissions: hasPermission(Permission.X)
 *
 * System admin bypass and view-only mode are automatically enforced inside hasPermission().
 *
 * ADMIN ROLE TESTING: System admins can temporarily override their role for testing.
 * The override is client-side only and auto-disables when switching FBOs.
 *
 * @returns Object with hasPermission function and permission context
 *
 * @example
 * const { hasPermission } = usePermissions();
 * if (hasPermission(Permission.MODIFY_LAYOUT)) {
 *   // User can edit layout
 * }
 */
export function usePermissions() {
  const { activeFBO } = useActiveFBOs();
  const { airplxIdentity } = useIdentity();
  const { isActive, overriddenRole, fboId, stopRoleOverride } = useRoleOverride();

  // Auto-disable role override when switching FBOs
  useEffect(() => {
    if (isActive && fboId && activeFBO?.id && fboId !== activeFBO.id) {
      stopRoleOverride();
    }
  }, [isActive, fboId, activeFBO?.id, stopRoleOverride]);

  const context: PermissionContext = useMemo(
    () => {
      // Only apply role override if:
      // 1. Override is active
      // 2. User is system admin (safety check)
      // 3. There's an overridden role set
      // 4. FBO IDs match (or no FBO active)
      const shouldOverride =
        isActive &&
        airplxIdentity.isadmin &&
        overriddenRole &&
        (!activeFBO || !fboId || fboId === activeFBO.id);

      return {
        user: {
          // IMPORTANT: Keep isAdmin true - preserves admin escape hatch
          isAdmin: airplxIdentity.isadmin,
          userid: airplxIdentity.userid,
        },
        fbo: activeFBO && shouldOverride
          ? {
              ...activeFBO,
              // Override the user_role for permission checks
              user_role: overriddenRole,
            }
          : activeFBO,
        // Flag to skip admin bypass during role testing
        roleOverrideActive: shouldOverride,
      };
    },
    [airplxIdentity, activeFBO, isActive, overriddenRole, fboId]
  );

  return {
    /**
     * Check if the current user has a specific permission.
     *
     * Automatically handles:
     * - System admin bypass (always returns true for admins)
     * - View-only mode (returns false for write permissions when in view-only mode)
     * - Role-based permissions (checks user's role against FBO settings)
     */
    hasPermission: (permission: Permission) =>
      hasPermission(permission, context),

    /**
     * Permission context for advanced use cases.
     * Most components should use hasPermission() instead.
     */
    context,
  };
}
